var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createElement, memo, useState, useCallback, useEffect } from '@max/max';
import Button from '@hfe/max-button';
import Text from '@hfe/max-text';
import Sensor from '@max/mbc-sensor';
import { useActivitySubscription } from '../../hooks/useActivitySubscription';
import { BID_MAP, BID_TYPE, TRIGGER_DRAW_EVENT } from '../../constants';
import './index.scss';
import { useMonitorEngineContext } from '@nibfe/mcp-monitor-sdk';
export var SubscribeStatus;
(function (SubscribeStatus) {
    /** 未订阅 */
    SubscribeStatus["UNSUBSCRIBED"] = "unsubscribed";
    /** 已订阅 */
    SubscribeStatus["SUBSCRIBED"] = "subscribed";
})(SubscribeStatus || (SubscribeStatus = {}));
const SubscribeButton = memo((props) => {
    const { activityId, drawModuleInfo, subscribeConfig, style, onSubscribeStatusChange, onSubscribed, lxReport, fontColor = '#ffffff' } = props;
    const ctx = useMonitorEngineContext();
    const [loading, setLoading] = useState(false);
    const [subscribeStatus, setSubscribeStatus] = useState(SubscribeStatus.UNSUBSCRIBED);
    const [hasQueried, setHasQueried] = useState(false);
    const { onSubscribe, querySubscribe } = useActivitySubscription(activityId, subscribeConfig);
    const { nextDrawTimeStart: appointmentTime, inDrawTime } = drawModuleInfo || {};
    // 查询预约状态
    useEffect(() => {
        // console.log('查询预约状态', appointmentTime, inDrawTime, hasQueried);
        if (!appointmentTime || inDrawTime || hasQueried) {
            return;
        }
        const queryStatus = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const result = yield querySubscribe(appointmentTime);
                const status = result.isSubscribed ? SubscribeStatus.SUBSCRIBED : SubscribeStatus.UNSUBSCRIBED;
                setSubscribeStatus(status);
                setHasQueried(true);
            }
            catch (_a) {
                setSubscribeStatus(SubscribeStatus.UNSUBSCRIBED);
            }
        });
        queryStatus();
    }, [appointmentTime, inDrawTime, querySubscribe]);
    useEffect(() => {
        onSubscribeStatusChange && onSubscribeStatusChange(subscribeStatus);
    }, [subscribeStatus]);
    const handleSubscribe = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!appointmentTime) {
            return;
        }
        // 避免重复点击导致多次请求
        if (loading) {
            return;
        }
        // 上报点击埋点
        lxReport === null || lxReport === void 0 ? void 0 : lxReport(BID_MAP.SUBSCRIBE_BUTTON_MC, BID_TYPE.MC, {
            status: SubscribeStatus.UNSUBSCRIBED
        });
        setLoading(true);
        try {
            const success = yield onSubscribe(appointmentTime);
            if (success) {
                setSubscribeStatus(SubscribeStatus.SUBSCRIBED);
                onSubscribed === null || onSubscribed === void 0 ? void 0 : onSubscribed();
            }
        }
        finally {
            setLoading(false);
        }
    }), [appointmentTime, loading, onSubscribe, lxReport, onSubscribed]);
    // 监听 VapView 点击事件
    useEffect(() => {
        var _a;
        const handleTriggerDraw = () => {
            // 如果在非抽奖时间且未预约，触发预约
            if (!inDrawTime && subscribeStatus === SubscribeStatus.UNSUBSCRIBED && appointmentTime) {
                handleSubscribe();
            }
        };
        // 注册事件监听
        (_a = ctx === null || ctx === void 0 ? void 0 : ctx._eventInstance) === null || _a === void 0 ? void 0 : _a.on(TRIGGER_DRAW_EVENT, handleTriggerDraw);
        // 组件卸载时移除监听
        return () => {
            var _a;
            (_a = ctx === null || ctx === void 0 ? void 0 : ctx._eventInstance) === null || _a === void 0 ? void 0 : _a.off(TRIGGER_DRAW_EVENT, handleTriggerDraw);
        };
    }, [inDrawTime, subscribeStatus, appointmentTime, handleSubscribe, ctx]);
    // 如果当前在抽奖时间内，不显示预约按钮
    if (inDrawTime) {
        return null;
    }
    // 获取按钮文案
    const getButtonText = () => {
        let text = '已预约';
        if (subscribeStatus === SubscribeStatus.UNSUBSCRIBED) {
            text = '预约抽奖';
        }
        return text;
    };
    // 按钮是否禁用
    const isButtonDisabled = loading || !appointmentTime || subscribeStatus === SubscribeStatus.SUBSCRIBED;
    return <Sensor className="subscribe-button-wrapper" style={style} once onAppear={() => {
        lxReport === null || lxReport === void 0 ? void 0 : lxReport(BID_MAP.SUBSCRIBE_BUTTON_MV, BID_TYPE.MV, {
            status: subscribeStatus
        });
    }}>
        <Button className="subscribe-button" onPress={handleSubscribe} disabled={isButtonDisabled}>
          <Text className="subscribe-button-text" style={{
        color: fontColor
    }}>
            {getButtonText()}
          </Text>
        </Button>
    </Sensor>;
});
export default SubscribeButton;
